import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Switch,
  FormControlLabel,
  Grid,
  Chip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import ManagetNav from '../../components/ManagerNav/ManagerNav';
import ManagerDto from '../../interfaces/ManagerDto';
import ManagerApi from '../../api/ManagerApi';
import './ManagerView.scss';

interface ManagerFormData {
  Manager_Username: string;
  Manager_FirstName: string;
  Manager_LastName: string;
  Manager_Email: string;
  Manager_Active: boolean;
  Manager_CanViewPayments: boolean;
  Manager_CanManageSettings: boolean;
}

/** Mirrors the server rule (alphanumeric, 3–50) so a bad username is caught before the round trip. */
const USERNAME_PATTERN = /^[a-zA-Z0-9]{3,50}$/;

const ManagerView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const managerGuid = searchParams.get('Manager_guid');

  const [manager, setManager] = useState<ManagerDto | null>(null);
  const [formData, setFormData] = useState<ManagerFormData>({
    Manager_Username: '',
    Manager_FirstName: '',
    Manager_LastName: '',
    Manager_Email: '',
    Manager_Active: true,
    Manager_CanViewPayments: false,
    Manager_CanManageSettings: false
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);

  // Fetch manager details
  const fetchManager = async () => {
    if (!managerGuid) return;
    
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/manager/get/${managerGuid}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch manager: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const managerData = data.manager;
      
      if (!managerData) {
        throw new Error('Manager not found');
      }

      setManager(managerData);
      setFormData({
        Manager_Username: managerData.Manager_Username ?? '',
        Manager_FirstName: managerData.Manager_FirstName,
        Manager_LastName: managerData.Manager_LastName,
        Manager_Email: managerData.Manager_Email,
        Manager_Active: managerData.Manager_Active,
        Manager_CanViewPayments: managerData.Manager_CanViewPayments ?? false,
        Manager_CanManageSettings: managerData.Manager_CanManageSettings ?? false
      });

    } catch (err) {
      console.error('Error fetching manager:', err);
      setError(err instanceof Error ? err.message : 'Failed to load manager');
    } finally {
      setLoading(false);
    }
  };

  // Save manager changes
  const handleSave = async () => {
    if (!manager) return;
    
    // Basic validation
    if (!formData.Manager_FirstName.trim() || !formData.Manager_LastName.trim() || !formData.Manager_Email.trim()) {
      setError('First name, last name, and email are required fields');
      return;
    }

    if (!USERNAME_PATTERN.test(formData.Manager_Username.trim())) {
      setError('Username must be 3-50 characters, letters and numbers only');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.Manager_Email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/manager/edit/${manager.Manager_guid}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            Manager_Username: formData.Manager_Username.trim(),
            Manager_FirstName: formData.Manager_FirstName.trim(),
            Manager_LastName: formData.Manager_LastName.trim(),
            Manager_Email: formData.Manager_Email.trim(),
            Manager_Active: formData.Manager_Active,
            Manager_CanViewPayments: formData.Manager_CanViewPayments,
            Manager_CanManageSettings: formData.Manager_CanManageSettings,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to update manager: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const updatedManager = data.manager;
      setManager(updatedManager);
      setFormData({
        Manager_Username: updatedManager.Manager_Username ?? '',
        Manager_FirstName: updatedManager.Manager_FirstName,
        Manager_LastName: updatedManager.Manager_LastName,
        Manager_Email: updatedManager.Manager_Email,
        Manager_Active: updatedManager.Manager_Active,
        Manager_CanViewPayments: updatedManager.Manager_CanViewPayments ?? false,
        Manager_CanManageSettings: updatedManager.Manager_CanManageSettings ?? false,
      });
      
      setIsEditing(false);
      setSuccess('Manager updated successfully!');
      
    } catch (err) {
      console.error('Error updating manager:', err);
      setError(err instanceof Error ? err.message : 'Failed to update manager');
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (field: keyof ManagerFormData, value: string | boolean) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleCancel = () => {
    if (manager) {
      setFormData({
        Manager_Username: manager.Manager_Username ?? '',
        Manager_FirstName: manager.Manager_FirstName,
        Manager_LastName: manager.Manager_LastName,
        Manager_Email: manager.Manager_Email,
        Manager_Active: manager.Manager_Active,
        Manager_CanViewPayments: manager.Manager_CanViewPayments ?? false,
        Manager_CanManageSettings: manager.Manager_CanManageSettings ?? false
      });
    }
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleSendPasswordReset = async () => {
    if (!manager || !manager.Manager_Email) {
      setError('Manager email not available');
      return;
    }

    setSendingPasswordReset(true);
    setError(null);
    setSuccess(null);

    try {
      const message = await ManagerApi.requestPasswordReset(manager.Manager_Email);
      setSuccess(message);
    } catch (err) {
      console.error('Error sending password reset email:', err);
      setError(err instanceof Error ? err.message : 'Failed to send password reset email');
    } finally {
      setSendingPasswordReset(false);
    }
  };

  useEffect(() => {
    if (!managerGuid) {
      setError('No manager ID provided');
      return;
    }
    fetchManager();
  }, [managerGuid]);

  if (!managerGuid) {
    return (
      <>
        <ManagetNav />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error">No manager ID provided in URL</Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <ManagetNav />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Manager Details
          </Typography>
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : manager ? (
          <>
            {/* Manager Information Card */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Manager Information</Typography>
                {!isEditing && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={sendingPasswordReset ? <CircularProgress size={16} color="inherit" /> : <LockResetIcon />}
                      onClick={handleSendPasswordReset}
                      variant="outlined"
                      size="small"
                      color="warning"
                      disabled={sendingPasswordReset}
                    >
                      {sendingPasswordReset ? 'Sending reset email...' : 'Reset Password'}
                    </Button>
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                      variant="outlined"
                      size="small"
                    >
                      Edit Manager
                    </Button>
                  </Box>
                )}
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Username"
                    value={formData.Manager_Username}
                    onChange={(e) => handleFormChange('Manager_Username', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    variant={isEditing ? 'outlined' : 'filled'}
                    required={isEditing}
                    error={isEditing && !USERNAME_PATTERN.test(formData.Manager_Username.trim())}
                    helperText={
                      isEditing && !USERNAME_PATTERN.test(formData.Manager_Username.trim())
                        ? 'Username must be 3-50 characters, letters and numbers only'
                        : 'What this person types on the manager login screen. They can also sign in with their email address.'
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="First Name"
                    value={formData.Manager_FirstName}
                    onChange={(e) => handleFormChange('Manager_FirstName', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    variant={isEditing ? 'outlined' : 'filled'}
                    required={isEditing}
                    error={isEditing && !formData.Manager_FirstName.trim()}
                    helperText={isEditing && !formData.Manager_FirstName.trim() ? 'First name is required' : ''}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Last Name"
                    value={formData.Manager_LastName}
                    onChange={(e) => handleFormChange('Manager_LastName', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    variant={isEditing ? 'outlined' : 'filled'}
                    required={isEditing}
                    error={isEditing && !formData.Manager_LastName.trim()}
                    helperText={isEditing && !formData.Manager_LastName.trim() ? 'Last name is required' : ''}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Email"
                    value={formData.Manager_Email}
                    onChange={(e) => handleFormChange('Manager_Email', e.target.value)}
                    disabled={!isEditing}
                    fullWidth
                    variant={isEditing ? 'outlined' : 'filled'}
                    type="email"
                    required={isEditing}
                    error={isEditing && (!formData.Manager_Email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Manager_Email))}
                    helperText={
                      isEditing && !formData.Manager_Email.trim() 
                        ? 'Email is required' 
                        : isEditing && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Manager_Email)
                        ? 'Please enter a valid email address'
                        : ''
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.Manager_Active}
                        onChange={(e) => handleFormChange('Manager_Active', e.target.checked)}
                        disabled={!isEditing}
                      />
                    }
                    label="Active Manager"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.Manager_CanViewPayments}
                        onChange={(e) => handleFormChange('Manager_CanViewPayments', e.target.checked)}
                        disabled={!isEditing}
                      />
                    }
                    label="Can view payments (amounts + revenue)"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.Manager_CanManageSettings}
                        onChange={(e) => handleFormChange('Manager_CanManageSettings', e.target.checked)}
                        disabled={!isEditing}
                      />
                    }
                    label="Can manage firm settings (incl. court LOR auto-send)"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={`Manager ID: ${manager.Manager_guid}`}
                      color="info"
                      size="small"
                    />
                    {manager.Manager_Active ? (
                      <Chip
                        label="Active"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="Inactive"
                        color="error"
                        size="small"
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Action Buttons */}
              {isEditing && (
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Paper>
          </>
        ) : (
          <Alert severity="error">Manager not found</Alert>
        )}
      </Container>
    </>
  );
};

export default ManagerView;
